import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Container } from "@shared/components/Container";
import { SeoHead } from "@shared/components";
import { useCatalogBySlug } from "@shared/hooks/useCatalogs";
import { useRootCategoriesByCatalog } from "@shared/hooks/useCategories";
import { useHomeData } from "@shared/hooks/useHome";
import { slugToReadableName } from "@shared/utils";
import { generateBreadcrumbSchema } from "@shared/utils/schemaOrg";
import type { CategoryWithOptionalImage } from "@shared/types/category";

export function Categories() {
  const { slugCatalogo } = useParams<{ 
    slugCatalogo: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const categoriesFromState = location.state?.categories;

  // Validar si tenemos categorías del estado o necesitamos hacer petición
  const hasCategoriesFromState = Boolean(
    categoriesFromState && categoriesFromState.length > 0
  );

  // Hook para obtener catálogo por slug (solo si no hay categorías del estado)
  const {
    data: catalogData,
    isLoading: catalogLoading,
    error: catalogError,
  } = useCatalogBySlug(slugCatalogo || "", {
    enabled: !hasCategoriesFromState, // Solo hacer petición si no hay estado
  });

  // Hook para obtener solo categorías root del catálogo
  const {
    data: rootCategoriesData,
  } = useRootCategoriesByCatalog(catalogData?.id || "");

  // Usar categorías del estado o de la petición
  // Si catalogData es un array, tomar el primer elemento
  const catalog = Array.isArray(catalogData) ? catalogData[0] : catalogData;
  
  // Usar solo categorías root del hook o del estado
  const rootCategories = categoriesFromState || 
    (rootCategoriesData?.data?.items || []) || 
    (catalog?.categories || []);

  // Usar solo categorías root (ya filtradas por el hook)
  const categories = Array.isArray(rootCategories) ? rootCategories : [];

  // Obtener datos del navbar para verificar subcategorías (más rápido que hacer llamadas individuales)
  const { data: homeData } = useHomeData();
  const navbarCatalog = homeData?.navbar?.catalogs?.find(
    (cat) => cat.slug === slugCatalogo
  );

  // Convertir slug a nombre legible
  const catalogName = catalog?.name || slugToReadableName(slugCatalogo || "");
  const pageTitle = catalogName;
  const pageDescription = catalog?.description || 
    `Explora las categorías de ${catalogName} en Cerámicas Gassull. Productos de construcción de alta calidad en Mendoza, Argentina.`;
  const canonicalUrl = `/catalog/${slugCatalogo}`;
  
  const breadcrumbItems = [
    { name: "Inicio", url: "/" },
    { name: catalogName, url: canonicalUrl },
  ];
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems);

  return (
    <>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        schemaJsonLd={breadcrumbSchema}
      />
      <div className="min-h-screen">
      {/* Header de la página */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {catalogName}
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Explora las categorías disponibles
            </p>
          </div>
        </div>
      </section>

      {/* Contenido principal */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {catalogLoading && !hasCategoriesFromState ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="h-56 bg-gray-200 rounded-lg animate-pulse"
                />
              ))}
            </div>
          ) : catalogError && !hasCategoriesFromState ? (
            <Container variant="default" padding="lg" className="text-center">
              <div className="text-red-600">
                <p>Error al cargar las categorías: {catalogError?.message}</p>
              </div>
            </Container>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category: CategoryWithOptionalImage) => {
                // Imagen por defecto para categorías sin imagen
                const defaultImage =
                  "https://res.cloudinary.com/dq8ig7aly/image/upload/v1749660976/no-image_lbt2of.jpg";
                const backgroundImage = category.image || defaultImage;

                // 🏷️ LOG: Categoría completa (data cruda)
                console.log("🏷️ [CATEGORY CARD] Categoría completa:", category);

                // Obtener datos de la categoría del navbar (incluye subcategorías)
                const navbarCategory = navbarCatalog?.categories?.find(
                  (cat) => cat.id === category.id || cat.slug === category.slug
                );
                const firstSubcategory = navbarCategory?.subcategories?.[0];

                // Función para manejar la navegación
                const handleCategoryClick = (e: React.MouseEvent) => {
                  e.preventDefault();
                  
                  // SIEMPRE navegar a productos
                  // Si tiene subcategorías, pasar la primera subcategoría en el estado para filtrar por defecto
                  // Si NO tiene subcategorías, navegar directamente sin filtros
                  navigate(`/catalog/${slugCatalogo}/categoria/${category.slug}`, {
                    state: {
                      subcategories: navbarCategory?.subcategories || [],
                      selectedSubcategoryId: firstSubcategory?.id || null,
                      categoryId: category.id,
                    },
                  });
                };

                return (
                  <div
                    key={category.id}
                    onClick={handleCategoryClick}
                    className="block cursor-pointer"
                  >
                    <Container
                      variant="category-card"
                      background={backgroundImage}
                      className="h-56 flex flex-col justify-between group hover:scale-105 transition-transform duration-200"
                      padding="lg"
                    >
                      {/* Nombre de la categoría centrado */}
                      <div className="flex-1 flex items-center justify-center">
                        <h3 className="text-white font-bold text-lg text-center leading-tight">
                          {category.name}
                        </h3>
                      </div>

                      {/* CTA en la esquina inferior derecha */}
                      <div className="flex justify-end">
                        <span className="text-white text-sm font-medium">
                          Ver productos →
                        </span>
                      </div>
                    </Container>
                  </div>
                );
              })}
            </div>
          ) : (
            <Container variant="default" padding="lg" className="text-center">
              <p className="text-gray-600">
                No hay categorías disponibles en este catálogo.
              </p>
            </Container>
          )}
        </div>
      </section>
    </div>
    </>
  );
}
